// components/minimal/MotionPage.tsx
//
// Page transition + interaction motion utilities for the minimal
// UI. Two surfaces here:
//
//   1) <MotionPage> — wraps a page's contents in an enter / exit
//      motion shell. Used by MinimalPageContainer so every route
//      change gets the same subtle fade + 8px upward slide. Pair
//      with <AnimatePresence> at the route level for exit motion.
//
//   2) <MotionStagger> + motion-friendly variants — for lists of
//      cards (e.g. tournaments on the dashboard) so they cascade
//      in instead of all popping at once. Distinct timing per
//      child via stagger.
//
// All motion respects prefers-reduced-motion via the MotionConfig
// at the app root (RootMotionProvider). Operators on motion-
// sensitive devices get the same layout with instant transitions.

import { ReactNode, ReactElement } from 'react';
import { motion, MotionConfig } from 'framer-motion';

const PAGE_ENTER = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
};

const LIST_PARENT = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.06 },
  },
};

const LIST_ITEM = {
  initial: { opacity: 0, y: 12 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

/**
 * Wrap a page's body in the standard enter/exit motion. Used
 * directly inside MinimalPageContainer so every minimal-mode
 * page gets the same subtle fade-and-slide. The key prop should
 * be the route path so React tears down the previous page on nav.
 */
export function MotionPage({
  children,
  pageKey,
}: {
  children: ReactNode;
  pageKey?: string;
}): ReactElement {
  return (
    <motion.div
      key={pageKey}
      initial={PAGE_ENTER.initial}
      animate={PAGE_ENTER.animate}
      exit={PAGE_ENTER.exit}
      transition={PAGE_ENTER.transition}
      style={{ width: '100%' }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Parent for a vertical list of cards / rows. Children should be
 * <MotionStaggerItem>s. The parent owns the timing; items inherit
 * stagger via the variants below.
 */
export function MotionStagger({
  children,
  delay = 0,
}: {
  children: ReactNode;
  delay?: number;
}): ReactElement {
  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={{
        initial: LIST_PARENT.initial,
        animate: {
          ...LIST_PARENT.animate,
          transition: {
            ...LIST_PARENT.animate.transition,
            delayChildren: delay || LIST_PARENT.animate.transition.delayChildren,
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Individual item inside a <MotionStagger>. Sequences in by 40ms
 * relative to the previous sibling. Doesn't take any props — pure
 * presentational wrapper.
 */
export function MotionStaggerItem({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  return (
    <motion.div variants={LIST_ITEM} style={{ width: '100%' }}>
      {children}
    </motion.div>
  );
}

/**
 * Sit this at the app root once. Sets global motion preferences:
 * respect prefers-reduced-motion (so accessibility users get the
 * same UI without any animations) and a default transition for
 * any motion.* component that doesn't specify its own.
 */
export function RootMotionProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  return (
    <MotionConfig
      reducedMotion="user"
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </MotionConfig>
  );
}
